import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { OrbisMarkSVG } from '@/components/OrbisLogo';
import UserMenu from '@/components/UserMenu';
import CreditMeter from '@/components/CreditMeter';
import ExpandingSearch from '@/components/dashboard/ExpandingSearch';
import {
  Search, X, Eye, ArrowRight,
  Layers, Sparkles, Menu, Bell,
  TrendingUp, Grid3x3, Code2,
  Zap,
} from 'lucide-react';
import {
  TEMPLATES, getFeaturedTemplates, getAllCategories, getAllStyles,
} from '@/data/templates';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_COLORS = {
  'Business':  '#10b981',
  'Creative':  '#f59e0b',
  'Finance':   '#3b82f6',
  'SaaS':      '#7c3aed',
  'Agency':    '#a855f7',
  'Portfolio': '#6366f1',
  'Startup':   '#06b6d4',
  'Restaurant':'#ff006e',
  'Med Spa':   '#C4956A',
  'Law Firm':  '#1e3a5f',
  'AI / Tech': '#06b6d4',
};

const SORT_OPTIONS = [
  { id: 'popular', label: 'Popular', icon: TrendingUp },
  { id: 'new',     label: 'New',     icon: Zap },
  { id: 'all',     label: 'All',     icon: Grid3x3 },
];

// ─── Skeleton card shown while loading ───────────────────────────────────────
function SkeletonCard() {
  return (
    <div style={{ background: '#0d0d0d', border: '1px solid #1a1a1a', borderRadius: '12px', overflow: 'hidden' }}>
      <div style={{ aspectRatio: '16/10', background: 'linear-gradient(90deg, #111 0%, #1a1a1a 50%, #111 100%)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite' }} />
      <div style={{ padding: '14px 16px' }}>
        <div style={{ height: 10, width: 60, background: '#1a1a1a', borderRadius: 4, marginBottom: 8 }} />
        <div style={{ height: 14, width: '80%', background: '#1a1a1a', borderRadius: 4, marginBottom: 6 }} />
        <div style={{ height: 12, width: '100%', background: '#141414', borderRadius: 4, marginBottom: 4 }} />
        <div style={{ height: 12, width: '70%', background: '#141414', borderRadius: 4, marginBottom: 12 }} />
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ height: 10, width: 50, background: '#141414', borderRadius: 4 }} />
          <div style={{ height: 10, width: 60, background: '#141414', borderRadius: 4 }} />
        </div>
      </div>
    </div>
  );
}

// ─── Main Template Card ─────────────────────────────────────��─────────────────
function TemplateCard({ template: t, index, onPreview, onUse }) {
  const navigate = useNavigate();
  const hasCategoryColor = CATEGORY_COLORS[t.category];
  const accentColor = hasCategoryColor || '#7c3aed';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.04, 0.4), duration: 0.3 }}
      className="group"
      style={{
        background: '#0d0d0d',
        border: '1px solid #1a1a1a',
        borderRadius: '12px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'border-color 0.2s, transform 0.2s',
      }}
      whileHover={{ y: -2, borderColor: '#2a2a2a' }}
      onClick={() => navigate(`/templates/${t.id}`)}
    >
      {/* Image area */}
      <div style={{ position: 'relative', aspectRatio: '16/10', overflow: 'hidden', background: '#111' }}>
        <img
          src={t.previewImage}
          alt={t.name}
          loading="lazy"
          onError={e => { e.target.src = `https://placehold.co/800x500/0d0d0d/444?text=${encodeURIComponent(t.name)}`; }}
          style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s ease', display: 'block' }}
          className="group-hover:scale-105"
        />

        {/* Tech stack badge — top left */}
        <div style={{ position: 'absolute', top: 8, left: 8 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '10px', fontFamily: 'var(--orbis-mono)', background: 'rgba(0,0,0,0.75)', color: '#aaa', padding: '2px 8px', borderRadius: '4px', backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Code2 size={9} /> {t.techStack || 'HTML5'}
          </span>
        </div>

        {/* Tier + New badges — top right */}
        <div style={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 4 }}>
          {t.isNew && (
            <span style={{ fontSize: '9px', padding: '2px 6px', borderRadius: '100px', background: '#1a0d1a', color: '#c084fc', border: '1px solid rgba(192,132,252,0.2)' }}>NEW</span>
          )}
          <span style={{
            fontSize: '9px', padding: '2px 7px', borderRadius: '100px',
            background: t.tier === 'pro' ? '#1a1500' : '#0d2010',
            color: t.tier === 'pro' ? '#f59e0b' : '#4ade80',
            border: `1px solid ${t.tier === 'pro' ? 'rgba(245,158,11,0.2)' : 'rgba(74,222,128,0.2)'}`,
          }}>
            {t.tier === 'pro' ? 'PRO' : 'FREE'}
          </span>
        </div>

        {/* Hover overlay */}
        <div
          className="opacity-0 group-hover:opacity-100"
          style={{
            position: 'absolute', inset: 0,
            background: 'rgba(0,0,0,0.7)',
            backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
            transition: 'opacity 0.25s',
          }}
        >
          <button
            onClick={e => { e.stopPropagation(); navigate(`/templates/${t.id}`); }}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.18)', background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: '12px', cursor: 'pointer', backdropFilter: 'blur(4px)' }}
          >
            <Eye size={13} /> Preview
          </button>
          <button
            onClick={e => { e.stopPropagation(); onUse(t); }}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: '8px', background: '#f0f0f0', color: '#000', fontSize: '12px', fontWeight: 600, border: 'none', cursor: 'pointer' }}
          >
            Use Template <ArrowRight size={13} />
          </button>
        </div>
      </div>

      {/* Card footer */}
      <div style={{ padding: '14px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: '10px', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.06em', color: accentColor }}>
            {t.category}
          </span>
          <span style={{ fontSize: '10px', color: '#444' }}>{t.style}</span>
        </div>
        <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#ccc', marginBottom: 4 }}>{t.name}</h3>
        <p style={{ fontSize: '12px', color: '#555', lineHeight: 1.45, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', marginBottom: 12 }}>
          {t.tagline}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '10px', color: '#3a3a3a' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Layers size={10} /> {t.pageCount} {t.pageCount === 1 ? 'page' : 'pages'}
          </span>
          <span>·</span>
          <span>{(t.popularity / 1000).toFixed(1)}k uses</span>
        </div>
      </div>
    </motion.div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────
export default function TemplatesPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeStyle, setActiveStyle] = useState('All');
  const [activeTier, setActiveTier] = useState('All');
  const [sortBy, setSortBy] = useState('popular');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  const categories = useMemo(() => ['All', ...getAllCategories()], []);

  // Fetch user projects for the header search
  useEffect(() => {
    axios.get(`${API_URL}/projects`).then(r => setProjects(r.data)).catch(() => {});
    // Simulate a brief loading state for skeleton cards
    const t = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(t);
  }, []);

  const filtered = useMemo(() => {
    let result = [...TEMPLATES];
    if (activeCategory !== 'All') result = result.filter(t => t.category === activeCategory);
    if (activeStyle !== 'All') result = result.filter(t => t.style === activeStyle);
    if (activeTier !== 'All') result = result.filter(t => t.tier === activeTier.toLowerCase());
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(t =>
        t.name.toLowerCase().includes(q) ||
        t.tagline.toLowerCase().includes(q) ||
        t.category.toLowerCase().includes(q) ||
        t.tags.some(tag => tag.includes(q))
      );
    }
    if (sortBy === 'popular') result.sort((a, b) => b.popularity - a.popularity);
    else if (sortBy === 'new') result = result.filter(t => t.isNew);
    return result;
  }, [activeCategory, activeStyle, activeTier, searchQuery, sortBy]);

  const featured = useMemo(() => getFeaturedTemplates(), []);

  const handleUseTemplate = (template) => {
    // Navigate to the editor — TemplateEditorPage handles project creation
    navigate(`/templates/${template.id}/edit`);
  };

  const clearFilters = () => {
    setActiveCategory('All');
    setActiveStyle('All');
    setActiveTier('All');
    setSearchQuery('');
    setSortBy('popular');
  };

  const activeFiltersCount = [
    activeCategory !== 'All',
    activeStyle !== 'All',
    activeTier !== 'All',
    searchQuery !== '',
  ].filter(Boolean).length;

  return (
    <div style={{ minHeight: '100vh', background: '#080808' }}>
      <style>{`
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes spin { to { transform: rotate(360deg); } }
        .shimmer { background:linear-gradient(90deg,#111 0%,#1a1a1a 50%,#111 100%);background-size:200% 100%;animation:shimmer 1.5s ease-in-out infinite; }
        ::-webkit-scrollbar { width: 0; height: 0; }
        .group:hover .group-hover\\:scale-105 { transform: scale(1.05); }
        .group:hover .group-hover\\:opacity-100 { opacity: 1; }
      `}</style>

      {/* ── Nav ── */}
      <nav style={{ position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', height: 64, background: 'var(--surface-0)', borderBottom: '0.5px solid var(--orbis-border)' }}>
        <div style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', left: 20, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => navigate('/dashboard')}>
          <OrbisMarkSVG size={22} />
          <span style={{ fontFamily: 'var(--orbis-font)', fontSize: '14px', fontWeight: 400, color: 'var(--orbis-t2)' }}>Orbis</span>
        </div>

        {/* Center nav tabs */}
        <div className="hidden md:flex glass-nav rounded-full items-center" style={{ padding: '4px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '0 8px' }}>
            {[
              { label: 'Home', onClick: () => navigate('/dashboard') },
              { label: 'Projects', onClick: () => navigate('/dashboard', { state: { tab: 'projects' } }) },
              { label: 'Templates', onClick: null, active: true },
            ].map(({ label, onClick, active }) => (
              <button
                key={label}
                onClick={onClick || undefined}
                style={{
                  padding: '5px 16px', borderRadius: '100px', fontSize: '13px',
                  fontWeight: active ? 500 : 400,
                  fontFamily: 'var(--orbis-font)',
                  color: active ? 'var(--orbis-t1)' : 'var(--orbis-t3)',
                  background: active ? 'rgba(250,250,250,0.06)' : 'transparent',
                  border: 'none', cursor: onClick ? 'pointer' : 'default',
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', right: 20, display: 'flex', alignItems: 'center', gap: 12 }}>
          <CreditMeter />
          <div className="hidden sm:flex">
            <ExpandingSearch projects={projects} onNavigate={(p) => {
              if (p.magic_mode) navigate(`/magic/${p.id}`);
              else if (p.clone_mode) navigate(`/clone/${p.id}`);
              else navigate(`/workspace/${p.id}`);
            }} />
          </div>
          <Bell size={14} className="hidden sm:block" style={{ color: 'var(--orbis-t3)' }} />
          <UserMenu />
        </div>
      </nav>

      {/* ── Page Header ── */}
      <div style={{ padding: '40px 40px 0' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
          <div>
            <h1 style={{ fontSize: '34px', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--orbis-t1)', fontFamily: 'var(--orbis-font)', marginBottom: 6 }}>
              Templates
            </h1>
            <p style={{ fontSize: '14px', color: 'var(--orbis-t3)' }}>
              Pick a template. Brand it. Ship it in minutes.
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ padding: '5px 14px', borderRadius: '100px', background: '#0d0d0d', border: '1px solid #1a1a1a', fontSize: '12px', color: 'var(--orbis-t4)' }}>
              {TEMPLATES.length} templates
            </span>
          </div>
        </div>
      </div>

      {/* ── Featured Strip ── */}
      {featured.length > 0 && (
        <div style={{ padding: '28px 40px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
            <Sparkles size={13} style={{ color: '#f59e0b' }} />
            <span style={{ fontSize: '11px', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#555' }}>Featured</span>
          </div>
          <div style={{ display: 'flex', gap: 16, overflowX: 'auto', scrollbarWidth: 'none', paddingBottom: 4 }}>
            {featured.map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                className="group"
                style={{ position: 'relative', flexShrink: 0, width: 380, height: 220, borderRadius: '12px', overflow: 'hidden', cursor: 'pointer' }}
                onClick={() => navigate(`/templates/${t.id}`)}
                whileHover={{ scale: 1.01 }}
              >
                <img src={t.previewImage} alt={t.name} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s' }} className="group-hover:scale-105" />
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '20px' }}>
                  <span style={{ fontSize: '10px', fontFamily: 'var(--orbis-mono)', textTransform: 'uppercase', letterSpacing: '0.06em', color: CATEGORY_COLORS[t.category] || '#7c3aed' }}>{t.category}</span>
                  <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#fff', marginTop: 2 }}>{t.name}</h3>
                  <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', marginTop: 3 }}>{t.tagline}</p>
                </div>
                {/* Featured hover CTA */}
                <div className="opacity-0 group-hover:opacity-100" style={{ position: 'absolute', top: 12, right: 12, transition: 'opacity 0.2s' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 12px', borderRadius: '20px', background: 'rgba(255,255,255,0.12)', color: '#fff', fontSize: '11px', backdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.15)' }}>
                    <Eye size={11} /> Preview
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* ── Filter Bar (Sticky) ── */}
      <div style={{ position: 'sticky', top: 0, zIndex: 40, background: '#080808', borderBottom: '1px solid #141414' }}>
        {/* Row 1: Centered Search */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '14px 40px 10px' }}>
          <div style={{ position: 'relative', width: '100%', maxWidth: 520 }}>
            <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#3a3a3a', pointerEvents: 'none' }} />
            <input
              type="text"
              placeholder="Search templates by name, category, or style…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                width: '100%', padding: '9px 36px 9px 36px', borderRadius: '10px', fontSize: '13px',
                background: '#0d0d0d', border: '1px solid #1e1e1e', color: '#ccc',
                fontFamily: 'var(--orbis-font)', outline: 'none', boxSizing: 'border-box',
              }}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#444' }}>
                <X size={13} />
              </button>
            )}
          </div>
        </div>

        {/* Row 2: Category pills + Sort toggle */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 40px 12px', gap: 16 }}>
          {/* Category pills */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflowX: 'auto', scrollbarWidth: 'none', flexShrink: 1 }}>
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                style={{
                  padding: '5px 14px', borderRadius: '100px', fontSize: '12px', fontWeight: activeCategory === cat ? 500 : 400,
                  fontFamily: 'var(--orbis-font)', border: activeCategory === cat ? 'none' : '1px solid #1a1a1a',
                  cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap', flexShrink: 0,
                  background: activeCategory === cat ? '#f0f0f0' : '#0d0d0d',
                  color: activeCategory === cat ? '#000' : '#555',
                }}
              >
                {cat}
              </button>
            ))}
          </div>

          {/* Controls: Tier + Sort */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {/* Tier toggle */}
            <div style={{ display: 'flex', alignItems: 'center', background: '#0d0d0d', border: '1px solid #1a1a1a', borderRadius: '8px', padding: '2px', gap: 1 }}>
              {['All', 'Free', 'Pro'].map(tier => (
                <button
                  key={tier}
                  onClick={() => setActiveTier(tier)}
                  style={{
                    padding: '4px 10px', borderRadius: '6px', fontSize: '11px', border: 'none', cursor: 'pointer',
                    fontFamily: 'var(--orbis-font)', transition: 'all 0.15s',
                    background: activeTier === tier ? '#1a1a1a' : 'transparent',
                    color: activeTier === tier ? '#ccc' : '#444',
                  }}
                >
                  {tier}
                </button>
              ))}
            </div>

            {/* Sort toggle */}
            <div style={{ display: 'flex', alignItems: 'center', background: '#0d0d0d', border: '1px solid #1a1a1a', borderRadius: '8px', padding: '2px', gap: 1 }}>
              {SORT_OPTIONS.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setSortBy(id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    padding: '4px 10px', borderRadius: '6px', fontSize: '11px', border: 'none', cursor: 'pointer',
                    fontFamily: 'var(--orbis-font)', transition: 'all 0.15s',
                    background: sortBy === id ? '#1a1a1a' : 'transparent',
                    color: sortBy === id ? '#ccc' : '#444',
                  }}
                >
                  <Icon size={10} /> {label}
                </button>
              ))}
            </div>

            {/* Clear filters */}
            {activeFiltersCount > 0 && (
              <button
                onClick={clearFilters}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '5px 10px', borderRadius: '6px', fontSize: '11px', background: 'transparent', border: '1px solid #2a2a2a', color: '#666', cursor: 'pointer' }}
              >
                <X size={10} /> Clear {activeFiltersCount > 1 ? `(${activeFiltersCount})` : ''}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Template Grid ── */}
      <div style={{ padding: '28px 40px 60px' }}>
        {isLoading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
            {Array.from({ length: 8 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : filtered.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 0', textAlign: 'center' }}
          >
            <div style={{ width: 64, height: 64, borderRadius: '16px', background: '#0d0d0d', border: '1px solid #1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
              <Search size={24} style={{ color: '#333' }} />
            </div>
            <p style={{ fontSize: '16px', fontWeight: 500, color: '#555', marginBottom: 8 }}>No templates found</p>
            <p style={{ fontSize: '13px', color: '#3a3a3a', marginBottom: 20 }}>Try adjusting your filters or search terms</p>
            <button
              onClick={clearFilters}
              style={{ padding: '8px 20px', borderRadius: '8px', background: '#0d0d0d', border: '1px solid #2a2a2a', color: '#888', fontSize: '13px', cursor: 'pointer' }}
            >
              Clear all filters
            </button>
          </motion.div>
        ) : (
          <>
            <p style={{ fontSize: '12px', color: '#3a3a3a', marginBottom: 16 }}>
              {filtered.length} template{filtered.length !== 1 ? 's' : ''}
              {activeFiltersCount > 0 ? ' matching filters' : ''}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '20px' }}>
              {filtered.map((t, i) => (
                <TemplateCard
                  key={t.id}
                  template={t}
                  index={i}
                  onPreview={(t) => navigate(`/templates/${t.id}`)}
                  onUse={handleUseTemplate}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
